import type { FontService, FontSubsetRequest } from '@/framework/services/font';
import type { FontLoadState } from './types';

const FONT_SUBSET_DEBOUNCE_MS = 350;

type FontLoaderOptions = {
    fontService: FontService;
    /**
     * 读取当前引擎字体版本；引擎未就绪时返回 null
     *
     * 当前 ensureFallbacks() 只返回 Promise<void>，缓存命中、注册成功与失败终态都会兑现，
     * 无法据此判断是否需要重排。因此比较准备前后的版本，仅在观察到字体状态变化时通知刷新，
     * 避免每次输入都额外重排；该版本属于整个引擎，变化也可能来自同期的字体子集注册。
     * null 则用于在加载入口暂缓请求，不推进 activeKey，待引擎就绪后由后续调用自然重试。
     *
     * 若不注入此查询，需要让 FontService 的准备结果表达字体状态是否变化，并由字体服务或
     * 调用方另行保证引擎就绪及后续重试；不能仅删除版本比较或在每次 Promise 兑现后无条件刷新。
     *
     * @returns 当前字体状态版本；引擎不可用时返回 null
     */
    getFontRevision: () => number | null;
    notifyInvalidated: () => void;
};

/**
 * 把未知异常转换成 Error。
 *
 * @param error - Promise 或字体服务抛出的未知异常。
 * @returns 可放入 failed state 的 Error。
 */
const normalizeError = (error: unknown): Error => {
    return error instanceof Error ? error : new Error(String(error));
};

/**
 * 创建未加载 subset 时使用的字体恒等映射。
 *
 * @param requests - 当前输入使用的字体请求。
 * @returns 原字体族映射到自身的只读 Map。
 */
const createIdentityMap = (requests: FontSubsetRequest[]): ReadonlyMap<string, string> => {
    return new Map(requests.map((request) => [request.fontFamily, request.fontFamily]));
};

/**
 * 管理单个 Text Renderer 的字体子集与按文本命中的 fallback 准备。
 */
export class FontLoader {
    /**
     * 当前输入的字体请求键，用于复用同一输入并阻止旧异步任务发布结果。
     */
    private activeKey: string | null = null;

    /**
     * latest-only 世代号；请求归属变化、重置或销毁时递增。
     */
    private generation = 0;

    /**
     * 当前加载器是否已销毁。
     */
    private disposed = false;

    /**
     * 当前 renderer 是否尚未执行首次字体加载。
     */
    private isInitialLoad = true;

    /**
     * 首次字体预备队列的取消控制器；进入底层即时加载后不再负责取消。
     */
    private preparationController: AbortController | null = null;

    /**
     * 当前输入对外可见的字体加载结果。
     */
    private state: FontLoadState = { status: 'pending' };

    /**
     * 后续字体子集加载使用的 trailing debounce 定时器。
     */
    private timer: ReturnType<typeof setTimeout> | null = null;

    /**
     * 创建 renderer 私有的字体加载器。
     *
     * @param options - 字体服务与 Renderer 失效通知。
     * @returns FontLoader 实例。
     */
    constructor(private readonly options: FontLoaderOptions) {}

    /**
     * 确保当前输入所需字体子集与 fallback 进入加载生命周期。
     *
     * @param requests - Pipeline 已按字体族汇总的子集请求。
     * @returns 当前字体加载状态与 ready 后的字体族映射。
     */
    ensure(requests: FontSubsetRequest[]): FontLoadState {
        // 未就绪时不推进请求身份，仍允许宿主复用上一份缓存画面。
        if (this.options.getFontRevision() === null) return this.state;
        const key = JSON.stringify(requests);

        // prepare 会清空 activeKey，因此相同字体 key 的同步输入也能接管 latest-only 所有权。
        if (key !== this.activeKey) this.start(key, requests);
        return this.state;
    }

    /**
     * 为需要原子提交布局的命令立即准备最终字体子集。
     *
     * 普通连续渲染使用原字体或旧 subset 并 debounce 加载新 subset；显式等待的命令必须拿到最终字体，
     * 否则会用旧字体尺寸提交候选图层。配置的 fallback 与子集并行准备，覆盖输入引入的新文种。
     *
     * @param requests - Pipeline 已按字体族汇总的子集请求。
     * @returns 最终字体映射；准备失败或被更新命令取代时返回对应非 ready 状态。
     */
    async prepare(requests: FontSubsetRequest[]): Promise<FontLoadState> {
        if (this.disposed) return { status: 'failed', error: new Error('Font loader has been disposed.') };
        if (this.options.getFontRevision() === null) return { status: 'pending' };

        const key = JSON.stringify(requests);

        // 原子布局必须同步消费首次机会，避免 await 期间的普通渲染再次进入首次预备队列。
        this.isInitialLoad = false;

        // 显式等待不占用普通渲染 owner；后续 ensure 必须能够重新取得所有权。
        this.activeKey = null;

        // 同时令普通渲染的 timer 或排队结果失去发布资格，原子路径只接受本次即时加载的最终结果。
        const generation = ++this.generation;

        this.cancelSchedule();
        this.cancelInitialPreparation();

        const state = await this.loadFinalFontFamilies(requests);
        if (this.disposed || generation !== this.generation) return { status: 'pending' };

        this.publishPreparedState(key, state);
        return this.state;
    }

    /**
     * 以只读方式等待最终字体子集，不参与普通渲染的所有权竞争。
     *
     * 只通过 FontService 加载子集与 fallback，不改写普通渲染的所有权和字体状态，
     * 不取消 debounce 或首次预备队列；同一子集的在途请求由 FontService 合并。
     *
     * @param requests - Pipeline 已按字体族汇总的子集请求。
     * @returns 最终字体映射；引擎未就绪或加载器已释放时返回 pending，子集加载失败时返回 failed。
     */
    async prepareDetached(requests: FontSubsetRequest[]): Promise<FontLoadState> {
        if (this.disposed) return { status: 'failed', error: new Error('Font loader has been disposed.') };
        if (this.options.getFontRevision() === null) return { status: 'pending' };

        const state = await this.loadFinalFontFamilies(requests);
        if (this.disposed) return { status: 'pending' };
        return state;
    }

    /**
     * 令当前字体请求失去发布资格，同时保留 owner 供后续非空输入复用。
     *
     * @returns void。
     */
    reset() {
        if (this.disposed) return;

        this.generation += 1;

        this.cancelSchedule();
        this.cancelInitialPreparation();

        this.activeKey = null;
        this.state = { status: 'pending' };
    }

    /**
     * 终止 timer，并令所有在途字体请求失去发布资格。
     *
     * @returns void。
     */
    dispose() {
        if (this.disposed) return;

        this.disposed = true;
        this.generation += 1;

        this.cancelSchedule();
        this.cancelInitialPreparation();

        this.activeKey = null;
    }

    /**
     * 为新的普通渲染输入启动字体子集加载流程。
     *
     * 该方法由 `ensure()` 在字体请求发生变化时调用。它会先取消上一轮尚未开始的定时任务和
     * 首次预备请求，再递增 generation，使此前已经开始的异步任务无法发布过期结果。
     *
     * 普通渲染优先同步复用已注册且覆盖当前字符的 subset；未命中时沿用同字体的上一轮映射或原字体族，
     * 再交给 `schedule()` 延迟加载。全部命中缓存则无需调度。首次普通加载通过 `prepareSubset()`
     * 进入限并发队列，后续输入直接使用 `loadSubset()`。
     *
     * @param key - 当前全部字体与字符组成的请求键。
     * @param requests - 当前输入按字体族合并的子集请求。
     * @returns void。
     */
    private start(key: string, requests: FontSubsetRequest[]) {
        this.cancelSchedule();
        this.cancelInitialPreparation();

        // 新普通输入也是 latest-only 边界，必须同步阻止更早的原子加载在完成后重新发布旧字体映射。
        const generation = ++this.generation;

        // 只有第一次普通字体加载使用 prepareSubset() 进入限并发队列，后续输入直接使用 loadSubset()。
        const isInitialLoad = this.isInitialLoad;

        this.isInitialLoad = false;

        // 字体族未变化时继续使用上一轮 subset，避免等待新 subset 期间中断渲染。
        const previous = this.state.status === 'ready' ? this.state.fontFamilies : null;
        const reusable =
            previous && requests.every((request) => previous.has(request.fontFamily))
                ? new Map(requests.map((request) => [request.fontFamily, previous.get(request.fontFamily)!]))
                : null;

        let needsLoad = false;
        const fontFamilies = new Map(
            requests.map(({ fontFamily, chars }) => {
                const available = this.options.fontService.getAvailableSubset(fontFamily, chars);
                if (!available) needsLoad = true;
                return [fontFamily, available ?? reusable?.get(fontFamily) ?? fontFamily];
            }),
        );

        this.activeKey = key;
        this.state = {
            status: 'ready',
            fontFamilies,
        };

        // 主字体子集命中或未入库时，也可能首次输入新文种；fallback 不受 subset debounce 限制。
        const revision = this.options.getFontRevision();
        void this.options.fontService.ensureFallbacks(requests.map((request) => request.chars).join('')).then(() => {
            const currentRevision = this.options.getFontRevision();
            if (this.canPublish(generation) && currentRevision !== null && currentRevision !== revision) {
                this.options.notifyInvalidated();
            }
        });

        if (needsLoad) this.schedule(generation, requests, isInitialLoad);
    }

    /**
     * 延迟加载当前普通渲染输入所需的字体子集。
     *
     * 未在 FontService 中注册的字体不会请求 subset，继续使用原字体族。其余请求等待 debounce
     * 结束后并行加载：首次普通加载调用 `prepareSubset()` 进入全局限并发队列，后续加载直接调用
     * `loadSubset()`。首次预备请求携带取消信号，因此输入变化或 Renderer 销毁时可以移除尚未开始的任务。
     *
     * 全部请求完成后只允许当前 generation 发布结果。任一字体返回 null 或抛出异常时发布 failed；
     * 全部成功时发布新的字体族映射并调用 `onInvalidated()` 通知宿主重读最新输入。
     *
     * @param generation - 当前 latest-only 世代。
     * @param requests - 当前输入按字体族合并的子集请求。
     * @param isInitialLoad - 当前完整输入是否应进入首次字体预备队列。
     * @returns void。
     */
    private schedule(generation: number, requests: FontSubsetRequest[], isInitialLoad: boolean): void {
        const loadable = requests.filter((request) => this.options.fontService.getByFamily(request.fontFamily));
        if (loadable.length === 0) return;

        this.timer = setTimeout(() => {
            this.timer = null;

            const controller = isInitialLoad ? new AbortController() : null;
            this.preparationController = controller;

            void Promise.all(
                loadable.map(async (request) => ({
                    request,
                    fontFamily: await (controller
                        ? this.options.fontService.prepareSubset(request.fontFamily, request.chars, {
                              signal: controller.signal,
                          })
                        : this.options.fontService.loadSubset(request.fontFamily, request.chars)),
                })),
            )
                .then((results) => {
                    if (!this.canPublish(generation)) return;

                    const fontFamilies = new Map(createIdentityMap(requests));
                    for (const result of results) {
                        if (!result.fontFamily) {
                            this.publishFailure(
                                generation,
                                new Error(`Failed to load font subset for ${result.request.fontFamily}.`),
                            );
                            return;
                        }
                        fontFamilies.set(result.request.fontFamily, result.fontFamily);
                    }

                    this.state = { status: 'ready', fontFamilies };
                    this.options.notifyInvalidated();
                })
                .catch((error) => {
                    this.publishFailure(generation, error);
                })
                .finally(() => {
                    if (this.preparationController === controller) this.preparationController = null;
                });
        }, FONT_SUBSET_DEBOUNCE_MS);
    }

    /**
     * 将最新字体请求发布为可观察的 failed 状态。
     *
     * @param generation - 异步请求启动时的 latest-only 世代。
     * @param error - subset 抛出的未知异常。
     * @returns void。
     */
    private publishFailure(generation: number, error: unknown): void {
        if (!this.canPublish(generation)) return;
        this.state = { status: 'failed', error: normalizeError(error) };
        this.options.notifyInvalidated();
    }

    /**
     * 立即加载当前请求的最终字体子集与配置的 fallback，并组装字体族映射。
     *
     * 只负责加载，不读写任何所有权状态；由 `prepare()` 与 `prepareDetached()` 各自决定结果能否发布。
     *
     * @param requests - Pipeline 已按字体族汇总的子集请求。
     * @returns 全部子集就绪时返回 ready 映射；任一子集返回空或抛出异常时返回 failed。
     */
    private async loadFinalFontFamilies(
        requests: FontSubsetRequest[],
    ): Promise<Extract<FontLoadState, { status: 'ready' | 'failed' }>> {
        try {
            const fallback = this.options.fontService.ensureFallbacks(
                requests.map((request) => request.chars).join(''),
            );
            const results = await Promise.all(
                requests
                    .filter((request) => this.options.fontService.getByFamily(request.fontFamily))
                    .map(async (request) => ({
                        request,
                        fontFamily: await this.options.fontService.loadSubset(request.fontFamily, request.chars),
                    })),
            );
            await fallback;

            const fontFamilies = new Map(createIdentityMap(requests));
            for (const result of results) {
                if (!result.fontFamily) {
                    const error = new Error(`Failed to load font subset for ${result.request.fontFamily}.`);
                    return { status: 'failed', error };
                }
                fontFamilies.set(result.request.fontFamily, result.fontFamily);
            }
            return { status: 'ready', fontFamilies };
        } catch (error) {
            return { status: 'failed', error: normalizeError(error) };
        }
    }

    /**
     * 将显式等待得到的最终字体结果设为当前 renderer 的 ready 状态。
     *
     * @param key - 当前全部字体与字符组成的请求键。
     * @param state - 最终 ready 或 failed 字体状态。
     * @returns void。
     */
    private publishPreparedState(key: string, state: Extract<FontLoadState, { status: 'ready' | 'failed' }>): void {
        this.cancelSchedule();
        this.activeKey = key;
        this.state = state;
    }

    /**
     * 判断异步完成是否仍属于当前 renderer 的最新输入。
     *
     * @param generation - 当前 latest-only 世代。
     * @returns renderer 未释放且 generation 仍为最新时返回 true。
     */
    private canPublish(generation: number): boolean {
        return !this.disposed && this.generation === generation;
    }

    /**
     * 取消尚未开始的字体子集加载。
     *
     * @returns void。
     */
    private cancelSchedule(): void {
        if (this.timer === null) return;
        clearTimeout(this.timer);
        this.timer = null;
    }

    /**
     * 取消尚未进入底层即时加载的首次字体预备请求。
     *
     * FontService 只会取消仍在 preparation 队列中的请求；已经开始的底层加载继续沿用其自身生命周期。
     *
     * @returns void。
     */
    private cancelInitialPreparation(): void {
        this.preparationController?.abort();
        this.preparationController = null;
    }
}
